import React, { useCallback } from 'react';
import { URLS, Url } from '@wix/da-url';
import { useTranslation, Trans } from 'react-i18next';
import { PapiUser, PapiDeviation } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import PointsIcon from '@wix/da-shared-react/pkg/Icons/Points';
import Button from '@wix/da-shared-react/pkg/Button';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import BasePaywall from '../BasePaywall';
import CoreIconSmall from '@wix/da-shared-react/pkg/Icons/CoreIconSmall';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';

import s from './DeviationPaywall.scss';

export interface Props {
  isLiterature?: boolean;
  premiumFolderData?: PapiDeviation['premiumFolderData'];
  contentOwner: PapiUser;
  showPremiumFolderPopup: (folderId: number) => void;
  folderId?: number;
  biData?: any;
  children: React.ReactNode;
}

const DeviationPaywall: React.FC<Props> = ({
  contentOwner,
  isLiterature,
  showPremiumFolderPopup,
  premiumFolderData,
  biData,
  children,
}) => {
  const { t } = useTranslation();
  const newCheckout = useFlag('new_checkout');
  const {
    type,
    hasAccess,
    pointsPrice,
    dollarPrice,
    galleryId,
    galleryUrl,
    subproductId,
  } = premiumFolderData || {};
  const onShowPopup = useCallback(() => {
    if (galleryId) {
      showPremiumFolderPopup(galleryId);
    }
  }, [galleryId, showPremiumFolderPopup]);

  const basePaywallProps = {
    isLiterature,
    hasAccess: !!hasAccess,
  };

  return (
    <>
      {type === 'paid' && (
        <BasePaywall
          {...basePaywallProps}
          icon={<div className={s['illustration-pcp']} />}
          title={t('paywall.premium.deviation.title')}
          text={
            // prettier-ignore
            <Trans i18nKey="paywall.premium.deviation.text">
              To view, unlock its <a className={s['folder-link']} href={galleryUrl}>Premium Folder</a> - and
              support the artist!
            </Trans>
          }
          button={
            <Button
              view="b18"
              fullWidth={false}
              onClick={newCheckout ? undefined : onShowPopup}
              href={
                newCheckout && subproductId
                  ? Url.checkoutLink(subproductId)
                  : undefined
              }
              biData={biData}
            >
              <span className={s['buttonLabel']}>
                {t('paywall.premium.deviation.unlockFor')} ${dollarPrice} /{' '}
                <PointsIcon size={IconSize.TINY} disableDefaultColors />
                {pointsPrice}
              </span>
            </Button>
          }
        >
          {children}
        </BasePaywall>
      )}
      {type === 'watchers' && (
        <BasePaywall
          {...basePaywallProps}
          isLiterature={isLiterature}
          hasAccess={!!hasAccess}
          icon={<div className={s['illustration-watchers']} />}
          title={t('paywall.watchersOnly.deviation.title')}
          text={t('paywall.watchersOnly.deviation.text')}
          button={
            <WatchButton view="b1" fullWidth={false} user={contentOwner!} />
          }
        >
          {children}
        </BasePaywall>
      )}
      {type === 'privileged' && (
        <BasePaywall
          {...basePaywallProps}
          icon={<CoreIconSmall />}
          title={t('paywall.privileged.deviation.title')}
          text={
            // prettier-ignore
            <Trans i18nKey="paywall.privileged.deviation.text">
              To view, unlock its buy core!
            </Trans>
          }
          button={
            <Button view="b18" fullWidth={false} href={URLS.CORE_MEMBERSHIP}>
              <span className={s['buttonLabel']}>
                {t('paywall.privileged.deviation.buyCore')}
              </span>
            </Button>
          }
        >
          {children}
        </BasePaywall>
      )}
      {!type && children}
    </>
  );
};
DeviationPaywall.displayName = 'DeviationPaywall';

export default DeviationPaywall;
